// PartnerStatusTable — Team N (2026-05-11)
//
// 業者別学習状況 dashboard の 一覧 table。
// 各 partner の category / template_status / sample / approved / confidence /
// amount_paradigm / canonical_match を表示し、 row click で drill-down を起動。

import React from 'react';
import { MdSchool, MdCheckCircle, MdHelpOutline, MdWarning, MdInfo } from 'react-icons/md';
import type { PartnerLearningRow } from '../../api/client';

const fmtPct = (n: number | null | undefined): string =>
  n == null ? '—' : `${(n * 100).toFixed(0)}%`;

const STATUS_COLOR: Record<string, string> = {
  promoted: '#338650',
  draft: '#7c5cff',
  demoted: '#c33939',
};

const PARADIGM_LABEL: Record<string, string> = {
  current_month_only: '当月のみ',
  cumulative_minus_previous: '累計-繰越',
  current_month_tax_excluded: '当月税抜',
  transfer_amount: '振込額',
  other: 'その他',
};

const CATEGORY_LABEL: Record<string, string> = {
  food_vendor: '食材',
  utility: '光熱',
  repair: '修繕',
  pest_control: '害虫',
  consumable: '消耗品',
};

export interface PartnerStatusTableProps {
  partners: PartnerLearningRow[];
  selectedPartnerId: number | null;
  onSelect: (partnerId: number) => void;
}

export const PartnerStatusTable: React.FC<PartnerStatusTableProps> = ({
  partners,
  selectedPartnerId,
  onSelect,
}) => {
  if (!partners.length) {
    return (
      <div style={{ padding: '16px', fontSize: '0.85rem', color: '#8a8580' }}>
        該当する業者がありません。 filter を緩めるか、 partner_patterns_index に row を追加してください。
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', maxHeight: '640px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <thead>
          <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
            <th style={th}>業者名</th>
            <th style={{ ...th, textAlign: 'right' }}>deal</th>
            <th style={th}>category</th>
            <th style={th}>template</th>
            <th style={{ ...th, textAlign: 'right' }}>sample</th>
            <th style={{ ...th, textAlign: 'right' }}>approved</th>
            <th style={{ ...th, textAlign: 'right' }}>conf</th>
            <th style={th}>paradigm</th>
            <th style={th}>canonical</th>
            <th style={th}>curated</th>
            <th style={{ ...th, textAlign: 'right' }}>cadence</th>
          </tr>
        </thead>
        <tbody>
          {partners.map((p) => {
            const isSel = selectedPartnerId === p.partner_id;
            const status = p.template?.template_status || 'no_template';
            const statusColor = STATUS_COLOR[status] || '#8a8580';
            const matchPct = p.canonical_match.match_rate;
            const conf = p.template?.ai_confidence ?? null;
            return (
              <tr
                key={p.partner_id}
                onClick={() => onSelect(p.partner_id)}
                style={{
                  borderBottom: '1px solid #f0eeec',
                  cursor: 'pointer',
                  background: isSel ? '#eef4ff' : 'transparent',
                }}
              >
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {status === 'promoted' && <MdCheckCircle style={{ color: '#338650', flexShrink: 0 }} />}
                    {status === 'draft' && <MdSchool style={{ color: '#7c5cff', flexShrink: 0 }} />}
                    {status === 'demoted' && <MdWarning style={{ color: '#c33939', flexShrink: 0 }} />}
                    {status === 'no_template' && <MdHelpOutline style={{ color: '#8a8580', flexShrink: 0 }} />}
                    <span style={{ fontWeight: isSel ? 600 : 400 }}>{p.partner_name}</span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>id: {p.partner_id}</div>
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: p.deal_count >= 30 ? '#338650' : '#332f2c' }}>
                  {p.deal_count}
                </td>
                <td style={{ ...td, fontSize: '0.6875rem' }}>
                  {CATEGORY_LABEL[p.category_hint || ''] || p.category_hint || '—'}
                </td>
                <td style={td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: '#fff',
                    background: statusColor,
                  }}>
                    {status === 'no_template' ? '未学習' : status}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>{p.template?.sample_count ?? '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{p.template?.approved_count ?? '—'}</td>
                <td style={{
                  ...td,
                  textAlign: 'right',
                  fontWeight: 600,
                  color: conf == null ? '#8a8580' : conf >= 0.85 ? '#338650' : conf >= 0.7 ? '#7c5cff' : '#dd7a00',
                }}>
                  {fmtPct(conf)}
                </td>
                <td style={{ ...td, fontSize: '0.6875rem' }}>
                  {PARADIGM_LABEL[p.template?.amount_paradigm || ''] || p.template?.amount_paradigm || '—'}
                </td>
                <td style={{
                  ...td,
                  textAlign: 'center',
                  color: matchPct == null ? '#8a8580' : matchPct === 1 ? '#338650' : '#dd7a00',
                  fontWeight: 600,
                }}>
                  {matchPct == null ? '—' : matchPct === 1 ? '一致' : '不一致'}
                </td>
                <td style={td}>
                  {p.curated ? (
                    <span style={{ color: '#338650', fontWeight: 600 }}>有</span>
                  ) : (
                    <span style={{ color: '#8a8580' }}>—</span>
                  )}
                </td>
                <td style={{
                  ...td,
                  textAlign: 'right',
                  color: p.cadence.confidence == null ? '#8a8580' :
                         p.cadence.confidence >= 0.85 ? '#338650' :
                         p.cadence.confidence >= 0.7 ? '#7c5cff' : '#dd7a00',
                }}>
                  {fmtPct(p.cadence.confidence)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const th: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '0.6875rem',
  color: '#6b6560',
  fontWeight: 500,
  position: 'sticky',
  top: 0,
  background: '#f7f5f5',
  borderBottom: '1px solid #e0dcd8',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
